import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@/context/auth-context';
import { useConsent } from '@/lib/consent';
import { trackEvent, trackPageView, setUserProperties, trackConversion } from '@/lib/analytics';

// Google Analytics and Google Ads configuration
const GA_MEASUREMENT_ID = 'G-JLV8V0WR0K';
const GOOGLE_ADS_ID = 'AW-1018882741';

declare global {
  interface Window {
    dataLayer: Array<unknown>;
    gtag: (command: string, ...args: unknown[]) => void;
  }
}

// Set up consent mode - gtag is already loaded via HTML
const setupConsentMode = () => {
  if (typeof window === 'undefined') return;
  
  // Ensure gtag is available (it should be from the HTML script)
  if (!window.gtag) {
    console.warn('[Analytics] gtag not found, consent mode setup skipped');
    return;
  }
  
  // Set default consent state
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
  
  // Update config to not send page views automatically
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false, // We'll handle page views manually
    cookie_domain: 'auto',
    cookie_flags: 'SameSite=None;Secure',
  });
};



export function GoogleAnalytics() {
  const [location] = useLocation();
  const prevLocation = useRef<string | null>(null);
  const { user } = useAuth();
  const { consent, hasUserConsented } = useConsent();
  const [isInitialized, setIsInitialized] = useState(false);

  // Set up consent mode on mount
  useEffect(() => {
    if (!isInitialized) {
      setupConsentMode();
      setIsInitialized(true);
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Google Analytics consent mode configured');
      }
    }
  }, [isInitialized]);

  // Update consent when user makes consent choice
  useEffect(() => {
    if (isInitialized && hasUserConsented && typeof window !== 'undefined' && window.gtag) {
      // Update consent based on user preferences
      window.gtag('consent', 'update', {
        analytics_storage: consent.analytics ? 'granted' : 'denied',
        ad_storage: consent.marketing ? 'granted' : 'denied',
        ad_user_data: consent.analytics ? 'granted' : 'denied',
        ad_personalization: consent.marketing ? 'granted' : 'denied',
      });
      
      // Track consent granted event
      if (consent.analytics) {
        trackEvent('consent_granted', {
          consent_type: 'analytics',
          analytics_storage: consent.analytics,
          marketing_consent: consent.marketing,
        });
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Consent updated:', {
          analytics_storage: consent.analytics ? 'granted' : 'denied',
          ad_storage: consent.marketing ? 'granted' : 'denied',
          ad_user_data: consent.analytics ? 'granted' : 'denied',
          ad_personalization: consent.marketing ? 'granted' : 'denied',
        });
      }
    }
  }, [isInitialized, hasUserConsented, consent.analytics, consent.marketing]);

  // Set user properties when user changes (only if we have consent)
  useEffect(() => {
    if (isInitialized && consent.analytics && user) {
      setUserProperties(user);
    }
  }, [user, isInitialized, consent.analytics]);

  // Track page views when location changes (only if we have consent)
  useEffect(() => {
    if (isInitialized && consent.analytics) {
      const pageTitle = document.title || location.split('/').pop() || 'Unknown Page';
      
      trackPageView(location, pageTitle);
      
      // Also track as a custom event for more detailed analytics
      trackEvent('page_view', {
        page_path: location,
        page_title: pageTitle,
        page_referrer: prevLocation.current || document.referrer,
      });
      
      if (process.env.NODE_ENV === 'development') {
        console.debug('Tracking page view for:', location);
      }
    }
    
    // Update previous location
    prevLocation.current = location;
  }, [location, isInitialized, consent.analytics]);

  // Track initial page load performance (only if we have consent)
  useEffect(() => {
    if (!isInitialized || !consent.analytics) return;
    
    // Track performance metrics after a short delay to ensure page is fully loaded
    const trackPerformance = () => {
      if (typeof window !== 'undefined' && window.performance) {
        try {
          const perfData = window.performance.timing;
          const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
          
          if (pageLoadTime > 0) {
            trackEvent('page_load_timing', {
              page_load_time: pageLoadTime,
              dns_time: perfData.domainLookupEnd - perfData.domainLookupStart,
              connect_time: perfData.connectEnd - perfData.connectStart,
              ttfb: perfData.responseStart - perfData.requestStart,
              dom_load_time: perfData.domComplete - perfData.domLoading,
              url: window.location.pathname,
            });
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Error tracking performance:', error);
          }
        }
      }
    };

    // Run after component mount and page is stable
    const timeoutId = setTimeout(trackPerformance, 1000);
    return () => clearTimeout(timeoutId);
  }, [isInitialized, consent.analytics]);

  return null;
}

// Re-export utility functions for backward compatibility
export { trackEvent, trackPageView, setUserProperties, trackConversion };